import { useCallback, useEffect, useRef } from 'react'
import { AIAgentType } from '../../../../../models/AIAgent'
import {
  EditedResult,
  MeetingSummaryExecutionResult,
  MeetingSummaryInteraction,
  MeetingSummaryInteractionResult,
  MeetingSummaryInteractionType,
  MeetingSummaryResult,
} from '../../../../../models/MeetingSummary'
import { parseMeetingSummary } from './utils'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { selectRoomSession, updateRoomSessionMetadata } from '../../../../../store/meetingSummary/roomSession'
import { selectSelectedMeetingTemplate } from '../../../../../store/meetingSummary/meetingTemplates'
import { useExecuteAiAgentMutation, useInteractWithAiAgentMutation, useInteractWithAiAgentStreamingMutation } from '../../../../../store/ai/aiAgent'
import {
  selectMeetingSummaryAgentAiAgentId,
  selectMeetingSummaryExportingToPDF,
  setAccumulatedMeetingSummary,
  setMeetingSummary,
  setMeetingSummaryAgentLoading,
  setMeetingSummaryAgentLoadingInteraction,
  setMeetingSummaryAiAgentId,
  setMeetingSummaryElements,
  setMeetingSummaryElementsToAccumulatedSummary,
  setMeetingSummaryExportingToPDF,
} from '../../../../../store/meetingSummaryAgent/meetingSummaryAgent'
import { addMessage, selectLastUserChatMessage, setMessages, updateStreamingMessage } from '../../../../../store/streamingChat/streamingChat'
import { TrackEvent, useTracking } from '../../common/tracking'
import { useLazyGetRoomSessionQuery, useLazyListAiResultsQuery } from '../../../../../store/media/media'
import { setAiResults } from '../../../../../store/meetingSummary/aiResults'
import { AiFeature } from '../../../../../models/AiFeature'
import { useExportToPDF } from '../../../../../components/PDFExportButton/hooks'
import { getRoomTrackingDetails } from '../../tracking'
import { useLazyGetAiAgentResultQuery } from '../../../../../store/ai/aiAgentResult'
import { setTasks } from '../../../../../store/task/task'
import { useLazyGetAllTasksQuery } from '../../../../../store/meetingSupport/meetingSupport'
import { useStreamingChat } from '../../../../../views/AIAgent/hooks/useStreamingChat'

export const useMeetingSummary = () => {
  const dispatch = useAppDispatch()
  const { tracked } = useTracking()

  const accountId = useAppSelector(selectAccountId)
  const roomSession = useAppSelector(selectRoomSession)
  const selectedMeetingTemplate = useAppSelector(selectSelectedMeetingTemplate)
  const aiAgentId = useAppSelector(selectMeetingSummaryAgentAiAgentId)
  const lastUserInteraction = useAppSelector(selectLastUserChatMessage)

  const { roomId, id: sessionId } = roomSession ?? {}
  const { id: meetingTemplateId } = selectedMeetingTemplate ?? {}

  const onTextDelta = useCallback(
    (data: MeetingSummaryResult) => {
      const { message, summary } = data
      if (!message) {
        return
      }
      dispatch(updateStreamingMessage(message))
      dispatch(setAccumulatedMeetingSummary(summary || null))
    },
    [dispatch],
  )

  const onNewAiAgentId = useCallback(
    (newAiAgentId: string) => {
      dispatch(setMeetingSummaryAiAgentId(newAiAgentId))
    },
    [dispatch],
  )

  const { onStreamingEvent } = useStreamingChat<MeetingSummaryResult>({ onTextDelta, onNewAiAgentId })

  const [triggerExecuteAiAgent] = useExecuteAiAgentMutation()
  const [triggerInteractWithAiAgent] = useInteractWithAiAgentMutation()
  const [triggerInteractWithAiAgentStreaming] = useInteractWithAiAgentStreamingMutation()
  const [triggerGetAiResults] = useLazyListAiResultsQuery()
  const [triggerGetRoomSession] = useLazyGetRoomSessionQuery()
  const [triggerGetAiAgentResult] = useLazyGetAiAgentResultQuery()
  const [triggerGetTasks] = useLazyGetAllTasksQuery()

  const parseAgentMeetingSummaryResult = useCallback(
    (meetingSummaryResult: MeetingSummaryExecutionResult | MeetingSummaryInteractionResult, saveMessage: boolean = true) => {
      const {
        result: { summary, message },
      } = meetingSummaryResult
      if (summary) {
        const parsedElements = parseMeetingSummary(summary)
        dispatch(setMeetingSummaryElements(parsedElements))
      }
      if (saveMessage && message) {
        dispatch(addMessage({ type: 'agent', message }))
      }
    },
    [dispatch],
  )

  const refetchAiResults = useCallback(async () => {
    if (!roomId || !sessionId) {
      return
    }
    try {
      const { data: aiResults } = await triggerGetAiResults({
        accountId,
        roomId,
        sessionId,
        pageRequest: { page: 0, size: 500 },
      })
      if (!aiResults?.data) {
        return
      }
      dispatch(setAiResults(aiResults.data))
    } catch (e) {
      console.error(e)
    }
  }, [dispatch, triggerGetAiResults, accountId, roomId, sessionId])

  const refetchRoomSessionMetadata = useCallback(async () => {
    if (!roomId || !sessionId) {
      return
    }

    try {
      const roomSession = await triggerGetRoomSession({ accountId, roomId, sessionId }).unwrap()

      if (roomSession?.data?.metadata) {
        dispatch(updateRoomSessionMetadata(roomSession.data.metadata))
      }
    } catch (e) {
      console.error(e)
    }
  }, [dispatch, triggerGetRoomSession, accountId, roomId, sessionId])

  const refetchMeetingTasks = useCallback(async () => {
    if (!roomId || !sessionId) {
      return
    }
    try {
      const filters = { type: 'meeting', roomId, sessionId }
      const { data: tasks } = await triggerGetTasks({ accountId, filters }).unwrap()
      dispatch(setTasks(tasks))
    } catch (e) {
      console.error(e)
    }
  }, [accountId, dispatch, roomId, sessionId, triggerGetTasks])

  const executeMeetingSummary = useCallback(async () => {
    dispatch(setMeetingSummaryAgentLoading(true))
    dispatch(setMessages([]))
    try {
      await triggerExecuteAiAgent({
        accountId,
        payload: {
          type: AIAgentType.MEETING_SUMMARY,
          context: {
            roomId,
            sessionId,
            meetingTemplateId,
          },
        },
      }).unwrap()

      const existingMeetingSummary = await triggerGetAiAgentResult(
        {
          accountId,
          type: AIAgentType.MEETING_SUMMARY,
          resultKey: `${roomId}|${sessionId}|${meetingTemplateId}`,
        },
        false,
      ).unwrap()
      if (existingMeetingSummary) {
        dispatch(setMeetingSummary(existingMeetingSummary))
        const { interaction } = existingMeetingSummary
        if (!interaction) {
          return
        }
        dispatch(
          setMessages(
            interaction.interactions.map((interaction) => {
              return { message: interaction.result.message, type: interaction.result.role }
            }),
          ),
        )
      }
    } finally {
      dispatch(setMeetingSummaryAgentLoading(false))
    }
  }, [dispatch, triggerExecuteAiAgent, accountId, roomId, sessionId, meetingTemplateId, triggerGetAiAgentResult])

  const _interactWithMeetingSummary = useCallback(
    async (interaction: MeetingSummaryInteraction, shouldSetLoadingInteraction: boolean = true) => {
      if (!aiAgentId) {
        return
      }
      if (shouldSetLoadingInteraction) {
        dispatch(setMeetingSummaryAgentLoadingInteraction(interaction.type))
      }
      try {
        const { type } = interaction
        if (type === 'chat') {
          dispatch(addMessage({ type: 'user', message: interaction.query })) // TODO api returns interactions
        }
        const meetingSummaryInteractionResult = (await triggerInteractWithAiAgent({
          accountId,
          aiAgentId,
          payload: interaction,
        }).unwrap()) as MeetingSummaryInteractionResult
        const {
          newAiAgentId,
          result: { summary },
        } = meetingSummaryInteractionResult
        if (newAiAgentId) {
          dispatch(setMeetingSummaryAiAgentId(newAiAgentId))
        }
        const saveMessage = type === 'chat' // TODO api returns interactions
        parseAgentMeetingSummaryResult(meetingSummaryInteractionResult, saveMessage)
        if (summary) {
          await refetchAiResults()
          await refetchMeetingTasks()
          await refetchRoomSessionMetadata()
        }
      } catch (e) {
        console.error(e)
      } finally {
        if (shouldSetLoadingInteraction) {
          dispatch(setMeetingSummaryAgentLoadingInteraction(null))
        }
      }
    },
    [
      aiAgentId,
      dispatch,
      triggerInteractWithAiAgent,
      accountId,
      parseAgentMeetingSummaryResult,
      refetchAiResults,
      refetchMeetingTasks,
      refetchRoomSessionMetadata,
    ],
  )

  const resubmitLastInteraction = useCallback(async () => {
    if (!aiAgentId || !lastUserInteraction) {
      return
    }
    try {
      dispatch(setMeetingSummaryAgentLoadingInteraction(MeetingSummaryInteractionType.CHAT))
      const payload = { type: MeetingSummaryInteractionType.CHAT, query: lastUserInteraction.message }
      await triggerInteractWithAiAgentStreaming({ accountId, aiAgentId, payload, onStreamingEvent })
    } catch (e) {
      console.error('Error while resubmiting last interaction:', e, lastUserInteraction)
    } finally {
      dispatch(setMeetingSummaryElementsToAccumulatedSummary())
      await refetchAiResults()
      await refetchMeetingTasks()
      await refetchRoomSessionMetadata()
      dispatch(setMeetingSummaryAgentLoadingInteraction(null))
    }
  }, [
    accountId,
    aiAgentId,
    dispatch,
    lastUserInteraction,
    onStreamingEvent,
    refetchAiResults,
    refetchMeetingTasks,
    refetchRoomSessionMetadata,
    triggerInteractWithAiAgentStreaming,
  ])

  const _streamingInteractWithMeetingSummary = useCallback(
    async (body: MeetingSummaryInteraction) => {
      if (!aiAgentId) {
        return
      }
      try {
        if (body.type === MeetingSummaryInteractionType.CHAT) {
          dispatch(addMessage({ type: 'user', message: body.query }))
          dispatch(setMeetingSummaryAgentLoadingInteraction(MeetingSummaryInteractionType.CHAT))
        }
        await triggerInteractWithAiAgentStreaming({ accountId, aiAgentId, payload: body, onStreamingEvent })
      } catch (e) {
        console.error('Streaming interaction error:', e)
      } finally {
        dispatch(setMeetingSummaryElementsToAccumulatedSummary())
        await refetchAiResults()
        await refetchMeetingTasks()
        await refetchRoomSessionMetadata()
        dispatch(setMeetingSummaryAgentLoadingInteraction(null))
      }
    },
    [aiAgentId, triggerInteractWithAiAgentStreaming, accountId, onStreamingEvent, dispatch, refetchAiResults, refetchMeetingTasks, refetchRoomSessionMetadata],
  )

  const streamingInteractWithMeetingSummary = tracked(
    _streamingInteractWithMeetingSummary,
    { eventName: TrackEvent.MEETING_SUMMARY_AGENT_INTERACT_STREAMING },
    {
      eventDetailsExtractor: (interaction: MeetingSummaryInteraction) => {
        return { type: interaction.type }
      },
    },
  )

  const interactWithMeetingSummary = tracked(
    _interactWithMeetingSummary,
    { eventName: TrackEvent.MEETING_SUMMARY_AGENT_INTERACT },
    {
      eventDetailsExtractor: (interaction: MeetingSummaryInteraction) => {
        return { type: interaction.type }
      },
    },
  )

  const handleMeetingSummaryResultEdited = useCallback(
    async (aiFeature: AiFeature, aiResultId: string, result: EditedResult, shouldSetLoadingInteraction: boolean = true) => {
      if (!aiAgentId) {
        return
      }
      await _interactWithMeetingSummary(
        {
          type: MeetingSummaryInteractionType.RESULT_EDIT,
          editedResult: {
            resultId: aiResultId,
            resultTypeId: aiFeature.id,
            resultTypeName: aiFeature.name,
            result,
          },
        },
        shouldSetLoadingInteraction,
      )
    },
    [_interactWithMeetingSummary, aiAgentId],
  )

  return {
    executeMeetingSummary,
    interactWithMeetingSummary,
    streamingInteractWithMeetingSummary,
    handleMeetingSummaryResultEdited,
    refetchAiResults,
    refetchRoomSessionMetadata,
    resubmitLastInteraction,
    refetchMeetingTasks,
  }
}

export const useMeetingSummaryExportToPDF = () => {
  const dispatch = useAppDispatch()

  const meetingSummaryExportingToPDF = useAppSelector(selectMeetingSummaryExportingToPDF)
  const roomSession = useAppSelector(selectRoomSession)

  const meetingSummaryAgentContentRef = useRef(null)
  const { tracked } = useTracking()
  const { exportToPDF: exportToPDF_ } = useExportToPDF(meetingSummaryAgentContentRef)

  const exportToPDF = tracked(exportToPDF_, {
    eventName: TrackEvent.EXPORT_TO_PDF,
    eventDetails: {
      ...getRoomTrackingDetails(roomSession),
      type: 'meetingSummary',
    },
  })

  useEffect(() => {
    if (meetingSummaryExportingToPDF) {
      exportToPDF()
    }
    dispatch(setMeetingSummaryExportingToPDF(false))
  }, [meetingSummaryExportingToPDF, dispatch, exportToPDF])

  return { meetingSummaryAgentContentRef }
}
